import { useMemo, ReactNode } from 'react';
import styles from './index.module.scss';
import getLocal from '@/app/utils/getLocal';
import { useLang } from '@/i18n';
import EcoButton from 'eco-design/base/eco-button';
import EcoImg from 'eco-design/base/eco-img';
import { useReferralStore } from '@/components/Referral/ReferralProvider';

interface BannerProps {
    subtitle?: string;
    title?: string;
    desc?: string;
    children?: ReactNode;
    /** 是否使用 dangerouslySetInnerHTML 渲染文本 */
    isInner?: boolean;
}

const ImgMap: any = {
    us: {
        pc: 'https://websiteoss.ecoflow.com/figma-uploads/1766373517829-qpuibv-compressed-Rectangle%203471158.jpg',
        pad: 'https://websiteoss.ecoflow.com/figma-uploads/1766373536074-lw8bu8-compressed-US%20%26%20CA_Pad.jpg',
        h5: 'https://websiteoss.ecoflow.com/figma-uploads/1766373554408-xtczll-compressed-US%20%26%20CA_Mob.jpg',
    },
    uk: {
        pc: 'https://websiteoss.ecoflow.com/figma-uploads/1767754211368-5fzkc3-compressed-01_KV_PC.jpg',
        pad: 'https://websiteoss.ecoflow.com/figma-uploads/1767754222593-vdxryx-compressed-01_KV_M.jpg',
        h5: 'https://websiteoss.ecoflow.com/figma-uploads/1767754222593-vdxryx-compressed-01_KV_M.jpg',
    },
    eu: {
        pc: 'https://websiteoss.ecoflow.com/figma-uploads/1766650864969-bg8wkr-compressed-EU.jpg',
        pad: 'https://websiteoss.ecoflow.com/figma-uploads/1766650875839-8p0vgo-compressed-EU_Pad.jpg',
        h5: 'https://websiteoss.ecoflow.com/figma-uploads/1766650885373-5bw4m5-compressed-EU_Mob.jpg',
    },
    kr: {
        pc: 'https://websiteoss.ecoflow.com/figma-uploads/1766650900447-nzwojz-compressed-KR%26JP.jpg',
        pad: 'https://websiteoss.ecoflow.com/figma-uploads/1766650920626-hooyzq-compressed-1%2061.jpg',
        h5: 'https://websiteoss.ecoflow.com/figma-uploads/1766650932563-n6mgbo-compressed-KR%26JP_Mob.jpg',
    },
    au: {
        pc: 'https://websiteoss.ecoflow.com/figma-uploads/1766650952915-sh52g5-compressed-AU%26ZA.jpg',
        pad: 'https://websiteoss.ecoflow.com/figma-uploads/1766650966049-197b28-compressed-AU%26ZA_Pad.jpg',
        h5: 'https://websiteoss.ecoflow.com/figma-uploads/1766650979224-wyp4hn-compressed-AU%26ZA_Mob.jpg',
    },
    mx: {
        pc: 'https://websiteoss.ecoflow.com/figma-uploads/1766650993933-jekex6-compressed-MX.jpg',
        pad: 'https://websiteoss.ecoflow.com/figma-uploads/1766651015767-etnava-compressed-MX_Pad.jpg',
        h5: 'https://websiteoss.ecoflow.com/figma-uploads/1766651030043-rz6znn-compressed-MX_Mob.jpg',
    },
    ph: {
        pc: 'https://websiteoss.ecoflow.com/figma-uploads/1766651044751-alpifx-compressed-PH.jpg',
        pad: 'https://websiteoss.ecoflow.com/figma-uploads/1766651060246-ck3q74-compressed-PH_Pad.jpg',
        h5: 'https://websiteoss.ecoflow.com/figma-uploads/1766651073985-gf7x8j-compressed-PH_Pad.jpg',
    },
};

ImgMap.ca = ImgMap.us;
ImgMap.jp = ImgMap.kr;
ImgMap.za = ImgMap.au;

ImgMap.de = ImgMap.eu;
ImgMap.it = ImgMap.eu;
ImgMap.es = ImgMap.eu;
ImgMap.fr = ImgMap.eu;

export default function Banner({ subtitle, title, desc, children, isInner = true }: BannerProps) {
    const { t } = useLang('Banner');
    const { locale, pathname } = getLocal();
    const { actStatus } = useReferralStore();

    const bgImgMap: { pc: string; pad: string; h5: string } = useMemo(() => {
        // 北美站点活动期间
        if (['us', 'ca'].includes(locale) && actStatus === 1) {
            return {
                pc: 'https://ecoflow-service-us-cdn.ecoflow.com/upload/image/1719213546128/na_dc_banner_pc.jpg',
                pad: 'https://ecoflow-service-us-cdn.ecoflow.com/upload/image/1719213551765/na_dc_banner_m.jpg',
                h5: 'https://ecoflow-service-us-cdn.ecoflow.com/upload/image/1719213551765/na_dc_banner_m.jpg',
            };
        }

        // 默认（us/ca 非活动期间及其他站点）
        return ImgMap[locale as keyof typeof bgImgMap];
    }, [locale, actStatus]);

    // 蓝色背景逻辑：特定路径 + 北美站点 + 活动中
    const isUseBg = useMemo(() => {
        const whitelist = ['/referral', '/referral-rewards'];
        const limitedSites = ['us', 'ca'];
        return whitelist.includes(pathname) && limitedSites.includes(locale) && actStatus === 1;
    }, [pathname, locale, actStatus]);

    // 默认文案
    const defaultTitle = '';
    const defaultDesc = '';

    const renderContent = () => {
        if (isInner) {
            return (
                <>
                    {subtitle && <p className="subtitle" dangerouslySetInnerHTML={{ __html: subtitle }} />}
                    <div className="title" dangerouslySetInnerHTML={{ __html: title || defaultTitle }} />
                    <div className="desc" dangerouslySetInnerHTML={{ __html: desc || defaultDesc }} />
                </>
            );
        }
        return (
            <>
                {subtitle && <p className="subtitle">{subtitle}</p>}
                <div className="title">{title || defaultTitle}</div>
                <div className="desc">{desc || defaultDesc}</div>
            </>
        );
    };

    return (
        <div className={`${styles.Banner} GtmModuleData-section`}>
            <EcoImg pc={bgImgMap.pc} pad={bgImgMap.pad} h5={bgImgMap.h5} />
            <div className={`text-wrapper ${isUseBg ? 'blue_bg' : ''}`}>
                {renderContent()}
                <div className="tools">{children || <EcoButton lRadius="ellipse" lTheme="white"></EcoButton>}</div>
            </div>
        </div>
    );
}
